import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { X, Plus, Edit } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, ImageUpload, ThemeColorPicker } from '@/components/ui';
import useAuthStore from '@/lib/stores/authStore';
import useRestaurantStore from '@/lib/stores/restaurantStore';
import { 
  restaurantFormSchema, 
  type RestaurantFormData,
  type RestaurantFormDataWithLogo
} from '@/lib/validations/restaurant';
import { validateImageFile } from '@/lib/firebase/storage';
import { getThemeColor } from '@/lib/utils/theme';
import type { Restaurant } from '@/types';

interface RestaurantModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'add' | 'edit';
  restaurant?: Restaurant | null;
}

/**
 * RestaurantModal - Modal component for adding and editing restaurants
 * 
 * Features:
 * - Handles both create and update operations
 * - Modal overlay with backdrop click to close
 * - Form validation and submission
 * - Image upload support
 */
export const RestaurantModal: React.FC<RestaurantModalProps> = ({
  isOpen,
  onClose,
  mode,
  restaurant,
}) => {
  const { user } = useAuthStore();
  const { 
    createRestaurant,
    updateRestaurant, 
    loading, 
    uploadProgress, 
    isUploading,
  } = useRestaurantStore();
  
  // Local state
  const [submissionError, setSubmissionError] = useState<string | undefined>(undefined);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoError, setLogoError] = useState<string | undefined>(undefined);
  const [currentThemeColor, setCurrentThemeColor] = useState<string>(() => 
    getThemeColor(restaurant?.themeColor)
  );
  
  // Form configuration
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    clearErrors,
    setError,
    setValue,
    reset,
  } = useForm<RestaurantFormData>({
    resolver: zodResolver(restaurantFormSchema),
    defaultValues: {
      name: '',
      description: '',
      phone: '',
      address: '',
      themeColor: undefined,
    },
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  // Reset form when modal opens/closes or mode changes
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && restaurant) {
        reset({
          name: restaurant.name,
          description: restaurant.description || '',
          phone: restaurant.phone || '',
          address: restaurant.address || '',
          themeColor: restaurant.themeColor,
        });
        setCurrentThemeColor(getThemeColor(restaurant.themeColor));
        // Sync the form with the current theme color
        setValue('themeColor', restaurant.themeColor || getThemeColor());
      } else {
        reset({
          name: '',
          description: '',
          phone: '',
          address: '',
          themeColor: undefined,
        });
        setCurrentThemeColor(getThemeColor());
        // Sync the form with the default theme color
        setValue('themeColor', getThemeColor());
      }
      setLogoFile(null);
      setLogoError(undefined);
      setSubmissionError(undefined);
    }
  }, [isOpen, mode, restaurant, reset]);

  // Form validation logic
  const watchedFields = watch();
  const hasValidFormData = 
    watchedFields.name && 
    watchedFields.name.trim() !== '' &&
    !errors.name &&
    !errors.description &&
    !errors.phone &&
    !errors.address &&
    !errors.themeColor &&
    !errors.root &&
    !logoError &&
    !submissionError;

  // Event handlers
  const handleFieldFocus = (fieldName: keyof RestaurantFormData) => {
    if (errors[fieldName]) {
      clearErrors(fieldName);
    }
    if (errors.root?.message) {
      clearErrors('root');
    }
    if (submissionError) {
      setSubmissionError(undefined);
    }
  };

  const handleThemeColorChange = (color: string) => {
    setCurrentThemeColor(color);
    // Update the form value and trigger validation
    setValue('themeColor', color, { shouldValidate: true });
  };

  const handleLogoSelect = (file: File | null) => {
    setLogoError(undefined);
    
    if (file) {
      const validation = validateImageFile(file);
      if (!validation.isValid) {
        setLogoError(validation.error || 'Invalid file');
        setLogoFile(null);
        return;
      }
    }
    
    setLogoFile(file);
  };

  const onSubmit = async (data: RestaurantFormData) => {
    if (!user) {
      setError('root', { message: 'Please sign in to continue' });
      return;
    }

    if (mode === 'edit' && !restaurant) {
      setError('root', { message: 'Restaurant not found' });
      return;
    }

    try {
      setSubmissionError(undefined);
      const formDataWithLogo: RestaurantFormDataWithLogo = {
        ...data,
        themeColor: currentThemeColor,
        logoFile,
      };
      
      if (mode === 'add') {
        await createRestaurant(user.uid, formDataWithLogo);
      } else {
        await updateRestaurant(restaurant!.id, formDataWithLogo as Partial<RestaurantFormDataWithLogo>);
      }
      
      onClose();
    } catch (error) {
      const errorMessage = mode === 'add' 
        ? 'Failed to create restaurant. Please try again.'
        : 'Failed to update restaurant. Please try again.';
      setError('root', { message: errorMessage });
      console.error(`Failed to ${mode} restaurant:`, error);
    }
  };

  const handleClose = () => {
    if (!isSubmitting && !loading && !isUploading) {
      onClose();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Configuration based on mode
  const config = {
    title: mode === 'add' ? 'Add Restaurant' : 'Edit Restaurant',
    submitText: mode === 'add' ? 'Add' : 'Update',
    loadingText: mode === 'add' ? 'Adding...' : 'Updating...',
    currentLogo: mode === 'edit' ? restaurant?.logoUrl : undefined,
    logoHelpText: mode === 'add'
      ? 'Upload a logo for your restaurant (JPEG, PNG, or WebP, max 5MB)'
      : 'Upload a new logo or keep the current one (JPEG, PNG, or WebP, max 5MB)',
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleBackdropClick}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          >
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, y: 100, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 100, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-2xl flex flex-col"
            >
              {/* Header - Fixed */}
              <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                    {mode === 'edit' ? (
                      <Edit className="w-5 h-5 text-white" />
                    ) : (
                      <Plus className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {config.title}
                  </h2>
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClose}
                  disabled={isSubmitting || loading || isUploading}
                  className="p-2"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Form Content */}
              <form 
                onSubmit={handleSubmit(onSubmit)} 
                className="flex-1 overflow-y-auto p-6 space-y-6" 
                id="restaurant-form"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
                    e.preventDefault();
                    if (hasValidFormData && !isSubmitting && !loading && !isUploading) {
                      handleSubmit(onSubmit)();
                    }
                  }
                }}
              >
                {/* Error Display */}
                <AnimatePresence mode="wait">
                  {(errors.root?.message || submissionError) && (
                    <motion.div
                      key="error-message"
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="bg-red-50 border border-red-200 rounded-lg p-4"
                    >
                      <p className="text-sm font-medium text-red-800">
                        {errors.root?.message || submissionError}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Restaurant Name */}
                <Input
                  {...register('name')}
                  label="Restaurant Name"
                  placeholder="e.g. Maharaja's Kitchen, Café Delight"
                  error={errors.name?.message}
                  onFocus={() => handleFieldFocus('name')}
                  required
                  autoFocus
                />

                {/* Logo Upload */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Restaurant Logo (Optional)
                  </label>
                  <ImageUpload
                    currentImageUrl={config.currentLogo}
                    onImageSelect={handleLogoSelect}
                    uploadProgress={uploadProgress}
                    isUploading={isUploading}
                    error={logoError}
                    helpText={config.logoHelpText}
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    {...register('description')}
                    placeholder="Brief description about your restaurant, cuisine, or specialties..."
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all duration-200 resize-none text-base"
                    onFocus={() => handleFieldFocus('description')}
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                  )}
                </div>

                {/* Phone */}
                <div className="relative">
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">
                      Phone Number
                      <span className="text-gray-400 ml-1">(Optional)</span>
                    </label>
                    <div className="relative">
                      <input
                        {...register('phone')}
                        type="tel"
                        placeholder="+91 99999 99999"
                        className="w-full h-10 px-4 text-base rounded-md text-gray-900 placeholder-gray-500 border border-gray-300 bg-white focus:border-primary focus:ring-1 focus:ring-primary transition-all duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                        onFocus={() => handleFieldFocus('phone')}
                      />
                    </div>
                    
                    {/* Error Message - Overlay style */}
                    {errors.phone && (
                      <motion.div
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        className="absolute top-full left-0 right-0 mt-1 z-10"
                      >
                        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 shadow-apple-sm">
                          <p className="text-sm text-red-600 font-medium">{errors.phone.message}</p>
                        </div>
                      </motion.div>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    Customers can call for reservations or inquiries
                  </p>
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address (Optional)
                  </label>
                  <textarea
                    {...register('address')}
                    placeholder="Enter your restaurant's full address..."
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all duration-200 resize-none text-base"
                    onFocus={() => handleFieldFocus('address')}
                  />
                  {errors.address && (
                    <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>
                  )}
                </div>

                {/* Theme Color */}
                <div className="border-t border-gray-200 pt-6">
                  <ThemeColorPicker
                    {...register('themeColor')}
                    value={currentThemeColor}
                    onChange={handleThemeColorChange}
                    error={errors.themeColor?.message}
                  />
                </div>
              </form>

              {/* Footer - Fixed */}
              <div className="flex space-x-3 p-6 pt-2 flex-shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleClose}
                  disabled={isSubmitting || loading || isUploading}
                  className="flex-1"
                >
                  Cancel
                </Button>
                
                <Button
                  type="submit"
                  form="restaurant-form"
                  variant="primary"
                  disabled={!hasValidFormData || isSubmitting || loading || isUploading}
                  loading={isSubmitting || loading || isUploading}
                  className="flex-1"
                >
                  {isUploading 
                    ? `Uploading Logo... ${Math.round(uploadProgress)}%`
                    : isSubmitting || loading 
                    ? config.loadingText
                    : config.submitText
                  }
                </Button>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
